import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaSpinner, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import api from '../../../services/api';
import { formatMiningDateTime } from '../../../utils/dateUtils';

const TransactionAllocationModal = ({ isOpen, onClose, transaction }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dispatchData, setDispatchData] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [showDispatchDetails, setShowDispatchDetails] = useState(false);
  const [showReceiptDetails, setShowReceiptDetails] = useState(false);
  const abortControllerRef = useRef(null);

  // Fetch detailed transaction data when modal opens
  useEffect(() => {
    if (isOpen && transaction) {
      fetchTransactionDetails();
    }

    // Cleanup on unmount or when modal closes
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isOpen, transaction?.ReceiptUID, transaction?.DispatchUID]); // Add specific dependencies

  const fetchTransactionDetails = async () => {
    if (!transaction) return;

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setDispatchData(null);
    setReceiptData(null);

    try {
      // Fetch both dispatch and receipt transaction details
      const promises = [];
      let tempReceiptData = null;
      let tempDispatchData = null;

      // Fetch receipt transaction details
      if (transaction.ReceiptUID) {
        promises.push(
          api.transactionAllocation
            .getTransactionDetails(transaction.ReceiptUID, {
              signal: abortControllerRef.current.signal,
            })
            .then((response) => ({ data: response.data, type: 'receipt' }))
            .catch((err) => ({ error: err, type: 'receipt' }))
        );
      }

      // Fetch dispatch transaction details if available
      if (transaction.DispatchUID) {
        promises.push(
          api.transactionAllocation
            .getTransactionDetails(transaction.DispatchUID, {
              signal: abortControllerRef.current.signal,
            })
            .then((response) => ({ data: response.data, type: 'dispatch' }))
            .catch((err) => ({ error: err, type: 'dispatch' }))
        );
      }

      if (promises.length > 0) {
        const results = await Promise.all(promises);

        // Process results and store in temp variables
        results.forEach((result) => {
          if (result.error) {
            console.warn(`Error fetching ${result.type} transaction:`, result.error);
          } else if (result.data) {
            if (result.data['Ticket Type'] === 'Receipt') {
              tempReceiptData = result.data;
            } else if (result.data['Ticket Type'] === 'Dispatch') {
              tempDispatchData = result.data;
            }
          }
        });
      }

      // If we couldn't fetch from API, create fallback data
      if (!tempReceiptData && transaction.ReceiptUID) {
        tempReceiptData = {
          'Ticket Timestamp': transaction.ReceiptTimestamp,
          'Ticket No': transaction.ReceiptTicketNo,
          'Delivery No': transaction.CurrentDeliveryNo,
          'Reg No': transaction.reg_no,
          'Nett Weight': null,
          'Ticket Type': 'Receipt',
        };
      }

      if (!tempDispatchData && transaction.DispatchUID && transaction.SuggestedTicketNo) {
        tempDispatchData = {
          'Ticket Timestamp': transaction.DispatchTimestamp,
          'Ticket No': transaction.SuggestedTicketNo,
          'Delivery No': transaction.SuggestedTicketNo,
          'Reg No': transaction.reg_no,
          'Nett Weight': null,
          'Ticket Type': 'Dispatch',
        };
      }

      // Set the state with the final data
      setReceiptData(tempReceiptData);
      setDispatchData(tempDispatchData);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching transaction details:', err);
        setError('Failed to load transaction details');

        // Even on error, try to show fallback data
        if (transaction.ReceiptUID) {
          setReceiptData({
            'Ticket Timestamp': transaction.ReceiptTimestamp,
            'Ticket No': transaction.ReceiptTicketNo,
            'Delivery No': transaction.CurrentDeliveryNo,
            'Reg No': transaction.reg_no,
            'Nett Weight': null,
            'Ticket Type': 'Receipt',
          });
        }

        if (transaction.DispatchUID && transaction.SuggestedTicketNo) {
          setDispatchData({
            'Ticket Timestamp': transaction.DispatchTimestamp,
            'Ticket No': transaction.SuggestedTicketNo,
            'Delivery No': transaction.SuggestedTicketNo,
            'Reg No': transaction.reg_no,
            'Nett Weight': null,
            'Ticket Type': 'Dispatch',
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Reset state when closing
    setLoading(false);
    setError(null);
    setDispatchData(null);
    setReceiptData(null);
    setShowDispatchDetails(false);
    setShowReceiptDetails(false);
    onClose();
  };

  const handleClose = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Reset state when closing
    setLoading(false);
    setError(null);
    setDispatchData(null);
    setReceiptData(null);
    setShowDispatchDetails(false);
    setShowReceiptDetails(false);
    onClose();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return formatMiningDateTime(timestamp) || 'N/A';
  };

  const formatWeight = (weight) => {
    if (weight === null || weight === undefined) return 'N/A';
    return `${parseFloat(weight).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} t`;
  };

  // Render transaction summary (basic fields)
  const renderTransactionSummary = (data, title, showDetails, setShowDetails) => {
    if (!data) {
      return (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-500">No data available</p>
        </div>
      );
    }

    return (
      <div className="bg-surface border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>

        {/* Basic Details - Always Visible */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">
                Ticket Timestamp
              </label>
              <p className="text-sm text-gray-900">{formatTimestamp(data['Ticket Timestamp'])}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Ticket No</label>
              <p className="text-sm text-gray-900">{data['Ticket No'] || 'N/A'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Delivery No</label>
              <p className="text-sm text-gray-900">{data['Delivery No'] || 'N/A'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Reg No</label>
              <p className="text-sm text-gray-900">{data['Reg No'] || 'N/A'}</p>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Nett Weight</label>
            <p className="text-sm text-gray-900">{formatWeight(data['Nett Weight'])}</p>
          </div>
        </div>

        {/* Show More Details Button */}
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="mt-4 flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
        >
          {showDetails ? (
            <>
              <FaChevronUp className="mr-1" />
              Show Less Details
            </>
          ) : (
            <>
              <FaChevronDown className="mr-1" />
              Show More Details
            </>
          )}
        </button>

        {/* Additional Details - Collapsible */}
        {showDetails && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  Dispatch Location
                </label>
                <p className="text-sm text-gray-900">{data['Dispatch Location'] || 'N/A'}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  Receipt Location
                </label>
                <p className="text-sm text-gray-900">{data['Receipt Location'] || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Product</label>
                <p className="text-sm text-gray-900">{data['Product'] || 'N/A'}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Customer</label>
                <p className="text-sm text-gray-900">{data['Customer'] || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Driver Name</label>
                <p className="text-sm text-gray-900">{data['Driver Name'] || 'N/A'}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Transporter</label>
                <p className="text-sm text-gray-900">{data['Transporter'] || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Gross Weight</label>
                <p className="text-sm text-gray-900">{formatWeight(data['Gross Weight'])}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Tare Weight</label>
                <p className="text-sm text-gray-900">{formatWeight(data['Tare Weight'])}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Transaction Details</h2>
            <p className="text-sm text-gray-600 mt-1">
              {transaction?.reg_no
                ? `Vehicle: ${transaction.reg_no}`
                : 'Transaction Allocation Details'}
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FaSpinner className="animate-spin w-8 h-8 text-primary-500 mx-auto mb-4" />
                <p className="text-gray-600">Loading transaction details...</p>
                <button
                  onClick={handleCancel}
                  className="mt-4 px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {!loading && !error && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Dispatch Transaction (Left Side) */}
              <div>
                {renderTransactionSummary(
                  dispatchData,
                  'Dispatch Transaction',
                  showDispatchDetails,
                  setShowDispatchDetails
                )}
              </div>

              {/* Receipt Transaction (Right Side) */}
              <div>
                {renderTransactionSummary(
                  receiptData,
                  'Receipt Transaction',
                  showReceiptDetails,
                  setShowReceiptDetails
                )}
              </div>
            </div>
          )}

          {/* Allocation Summary */}
          {!loading && !error && transaction && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-blue-900 mb-3">Allocation Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <label className="text-xs font-medium text-blue-700 uppercase">
                    Hours Difference
                  </label>
                  <p className="text-blue-900">
                    {transaction.HoursDifference !== null &&
                    transaction.HoursDifference !== undefined
                      ? `${transaction.HoursDifference.toFixed(1)} hours`
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-blue-700 uppercase">
                    Weight Variance
                  </label>
                  <p className="text-blue-900">
                    {transaction.WeightVariance !== null && transaction.WeightVariance !== undefined
                      ? `${transaction.WeightVariance.toFixed(2)} tonnes`
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-blue-700 uppercase">
                    Suggested Delivery No
                  </label>
                  <p className="text-blue-900 font-medium">
                    {transaction.SuggestedTicketNo || 'No suggestion'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionAllocationModal;
